import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CakeIcon from '@mui/icons-material/Cake';
import GroupsIcon from '@mui/icons-material/Groups';
import SchoolIcon from '@mui/icons-material/School';
import StraightenIcon from '@mui/icons-material/Straighten';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import candidates from '../data/candidates.json';
import candidateImages from '../assets/candidateImages';
import { useTranslation } from 'react-i18next';
import {
  Grid, Typography, Button,
  Box, Container, Stack, Divider, Tooltip,
  TextField, InputAdornment, IconButton,
  Dialog, DialogContent,
} from '@mui/material';

const THEME = {
  primary: '#2563eb',
  accent: '#f97316',
  dark: '#0f172a',
  light: '#f8fafc',
  border: '#e2e8f0',
};

const getProfesiColor = (profesi = '') => {
  const p = profesi.toLowerCase();
  if (p.includes('art') || p.includes('asisten') || p.includes('household')) return '#2563eb';
  if (p.includes('baby') || p.includes('nanny') || p.includes('sitter'))      return '#f97316';
  return '#059669';
};

const PROFESI_TKEY = {
  'Asisten Rumah Tangga': 'kandidatBanner.profesi.art',
  'Babysitter': 'kandidatBanner.profesi.babysitter',
  'Perawat Lansia': 'kandidatBanner.profesi.elderCare',
};

const getProfesiLabel = (profesi, t) => {
  const tKey = PROFESI_TKEY[profesi];
  return tKey ? t(tKey) : profesi;
};

/* ─── Modal ─────────────────────────────────────────────────────────── */
function ModalSectionLabel({ children, accent }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: '#0f172a', letterSpacing: '-0.01em', mb: 0.6 }}>
        {children}
      </Typography>
      <Box sx={{ width: 30, height: 3, bgcolor: accent || '#f97316', borderRadius: 1 }} />
    </Box>
  );
}

function CandidateModal({ candidate: k, onClose, onPrev, onNext, t }) {
  if (!k) return null;

  const color = getProfesiColor(k.profesi);
  const lightColor = color === '#2563eb' ? '#93c5fd' : color === '#f97316' ? '#fdba74' : '#6ee7b7';

  const waHref = `https://wa.me/${t('footer.number')}?text=${encodeURIComponent(
    t('kandidat.detail.ctaMessage', { name: k.namaLengkap, profesi: k.profesi })
  )}`;

  const personalInfo = [
    { label: t('kandidat.detail.labelPendidikan'), value: k.pendidikanTerakhir },
    { label: t('kandidat.detail.labelAsalKota'),   value: k.asal },
    { label: t('kandidat.detail.labelDomisili'),   value: k.domisili },
    { label: t('kandidat.detail.labelAgama'),      value: k.agama },
    { label: t('kandidat.detail.labelStatus'),     value: k.statusPernikahan },
    { label: t('kandidat.detail.labelAnak'),       value: k.punyaAnak },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', maxHeight: '92vh', m: { xs: 1.5, sm: 3 } } }}>

      {/* ── Compact dark header ── */}
      <Box sx={{
        position: 'relative', flexShrink: 0,
        background: [
          `radial-gradient(ellipse 70% 120% at 0% 50%, ${color}28 0%, transparent 60%)`,
          '#060d1f',
        ].join(', '),
        overflow: 'hidden',
        px: 2.5, py: 1.75,
      }}>
        {/* Accent bar */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #2563eb, #f97316)' }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}
          sx={{ position: 'relative', zIndex: 1 }}>

          {/* Left: badges + name */}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} sx={{ mb: 0.7 }}>
              <Box sx={{
                px: 1.2, py: 0.3,
                bgcolor: `${color}22`, border: `1px solid ${color}50`,
                borderRadius: '20px', color: lightColor,
                fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em',
              }}>
                {getProfesiLabel(k.profesi, t)}
              </Box>
              <Box sx={{
                px: 1.2, py: 0.3,
                bgcolor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.28)',
                borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#10b981' }} />
                <Typography sx={{ color: '#6ee7b7', fontSize: '0.63rem', fontWeight: 700 }}>
                  {k.statusSDM}
                </Typography>
              </Box>
            </Stack>
            <Typography sx={{
              color: '#fff', fontWeight: 900, fontSize: '1.05rem',
              lineHeight: 1.15, letterSpacing: '-0.02em',
            }}>
              {k.namaPanggilan}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', mt: 0.15 }}>
              {k.namaLengkap}
            </Typography>
          </Box>

          {/* Right: full-page link + close */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Button
              component={Link}
              to={`/kandidat/${k.slug}`}
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
              sx={{
                textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
                color: 'rgba(255,255,255,0.55)', px: 1,
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {t('kandidat.detail.fullPageButton')}
            </Button>
            <IconButton size="small" onClick={onClose}
              sx={{ color: 'rgba(255,255,255,0.55)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* ── Body ── */}
      <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
        <Grid container>

          {/* LEFT: photo + stats + CTA */}
          <Grid item xs={12} md={4} sx={{ bgcolor: '#fff', borderRight: { md: '1px solid #f1f5f9' } }}>
            {/* Photo */}
            <Box sx={{ position: 'relative', height: 260, overflow: 'hidden' }}>
              <Box
                component="img"
                src={candidateImages[k.slug] || k.foto}
                alt={k.namaLengkap}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
              />
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,0.70) 0%, transparent 55%)' }} />

              {/* Profesi pill on photo */}
              <Box sx={{
                position: 'absolute', top: 12, left: 12,
                px: 1.2, py: 0.4,
                bgcolor: 'rgba(10,15,30,0.70)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.14)', borderRadius: '20px',
                display: 'flex', alignItems: 'center', gap: 0.6,
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700 }}>{getProfesiLabel(k.profesi, t)}</Typography>
              </Box>

              <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 2, pb: 2 }}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.15rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                  {k.namaPanggilan}
                </Typography>
              </Box>
            </Box>

            {/* Stats + CTA */}
            <Box sx={{ p: 2.5 }}>
              {/* 2×2 stat grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2.5 }}>
                {[
                  { icon: <CakeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Usia',  val: `${k.umur} thn` },
                  { icon: <StraightenIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Tinggi', val: k?.tinggiBadan ? `${k.tinggiBadan} cm` : '–' },
                  { icon: <MonitorWeightIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Berat',  val: k?.beratBadan ? `${k.beratBadan} kg` : '–' },
                  { icon: <GroupsIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Suku',  val: k.suku },
                ].map((s, i) => (
                  <Box key={i} sx={{ p: 1.2, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                      {s.icon}
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {s.label}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a' }}>{s.val}</Typography>
                  </Box>
                ))}
              </Box>

              {/* WA CTA */}
              <Button
                fullWidth
                component="a"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon sx={{ fontSize: 17 }} />}
                sx={{
                  py: 1.3, borderRadius: '12px',
                  bgcolor: '#0f172a', color: '#fff',
                  fontSize: '0.85rem', fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(15,23,42,0.2)',
                  '&:hover': { bgcolor: color, boxShadow: `0 4px 16px ${color}44` },
                  transition: 'all 0.2s',
                }}
              >
                {t('kandidat.detail.ctaButton')}
              </Button>

              {/* Prev / Next navigation */}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  fullWidth
                  disabled={!onPrev}
                  onClick={onPrev}
                  startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    py: 1, borderRadius: '12px',
                    bgcolor: '#f1f5f9', color: '#475569',
                    fontSize: '0.78rem', fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#e2e8f0' },
                    '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1' },
                  }}
                >
                  {t('kandidat.detail.prevCandidate')}
                </Button>
                <Button
                  fullWidth
                  disabled={!onNext}
                  onClick={onNext}
                  endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    py: 1, borderRadius: '12px',
                    bgcolor: '#f1f5f9', color: '#475569',
                    fontSize: '0.78rem', fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#e2e8f0' },
                    '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1' },
                  }}
                >
                  {t('kandidat.detail.nextCandidate')}
                </Button>
              </Stack>
            </Box>
          </Grid>

          {/* RIGHT: detail sections */}
          <Grid item xs={12} md={8}>
            <Stack spacing={0} divider={<Divider sx={{ borderColor: '#f1f5f9' }} />}>

              {/* Personal info */}
              <Box sx={{ p: 2.5 }}>
                <ModalSectionLabel accent={color}>{t('kandidat.detail.sectionPersonal')}</ModalSectionLabel>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
                  {personalInfo.map((item, idx) => (
                    <Box key={idx} sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.35 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Skills */}
              <Box sx={{ p: 2.5 }}>
                <ModalSectionLabel accent={color}>{t('kandidat.detail.sectionSkills')}</ModalSectionLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {k.keahlian.map((skill, i) => (
                    <Box key={i} sx={{
                      px: 1.3, py: 0.5,
                      bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.78rem', fontWeight: 600, color: '#334155',
                    }}>
                      {skill}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Experience */}
              <Box sx={{ p: 2.5 }}>
                <ModalSectionLabel accent={color}>{t('kandidat.detail.sectionExperience')}</ModalSectionLabel>
                <Stack spacing={0} divider={<Divider sx={{ borderColor: '#f8fafc' }} />}>
                  {k.pengalaman.map((p, i) => (
                    <Box key={i} sx={{ py: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {/* Numbered circle */}
                      <Box sx={{
                        width: 30, height: 30, borderRadius: '50%',
                        bgcolor: `${color}14`, border: `2px solid ${color}38`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, mt: 0.2,
                      }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color }}>
                          {String(i + 1).padStart(2, '0')}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.6} sx={{ mb: 0.4 }}>
                          <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.88rem', lineHeight: 1.3 }}>{p.posisi}</Typography>
                          <Box sx={{
                            px: 1, py: 0.25, bgcolor: '#fff7ed', border: '1px solid #fed7aa',
                            borderRadius: '8px', fontSize: '0.66rem', fontWeight: 700, color: '#f97316',
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                            {p.durasi}
                          </Box>
                        </Stack>
                        <Typography sx={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.65 }}>{p.deskripsi}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* Notes */}
              {k.catatan && k.catatan.length > 0 && (
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AssignmentIcon sx={{ color: '#f97316', fontSize: 14 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, color: '#92400e', fontSize: '0.85rem' }}>
                        {t('kandidat.detail.sectionNotes')}
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      {k.catatan.map((note, i) => (
                        <Stack key={i} direction="row" alignItems="flex-start" spacing={1}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#f97316', mt: 0.3, flexShrink: 0 }} />
                          <Typography sx={{ fontWeight: 600, color: '#78350f', fontSize: '0.8rem', lineHeight: 1.65 }}>{note}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}

            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main list page ────────────────────────────────────────────────── */
export default function KandidatList() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  const { t } = useTranslation();
  const location = useLocation();
  const profesiOptions = [...new Set(candidates.map(c => c.profesi))];

  const initialProfesi = (() => {
    const p = location.state?.profesi;
    return p && profesiOptions.includes(p) ? [p] : [];
  })();

  const [searchTerm, setSearchTerm]         = useState('');
  const [selectedProfesi, setSelectedProfesi] = useState(initialProfesi);
  const [modalCandidate, setModalCandidate]  = useState(null);
  const [expandedPhotoId, setExpandedPhotoId] = useState(null);

  const toggleProfesi = (v) =>
    setSelectedProfesi(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const filtered = candidates.filter(item => {
    const matchSearch  = item.namaLengkap.toLowerCase().includes(searchTerm.toLowerCase());
    const matchProfesi = selectedProfesi.length === 0 || selectedProfesi.includes(item.profesi);
    return matchSearch && matchProfesi;
  });

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>

      {/* ── DARK HERO HEADER ── */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        background: [
          'radial-gradient(ellipse 60% 80% at 8% 50%, rgba(37,99,235,0.22) 0%, transparent 65%)',
          'radial-gradient(ellipse 50% 60% at 92% 20%, rgba(249,115,22,0.12) 0%, transparent 60%)',
          '#060d1f',
        ].join(', '),
      }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #2563eb, #f97316)' }} />
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 80% 90% at 30% 50%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 90% at 30% 50%, black 20%, transparent 100%)',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 14, md: 18 }, pb: { xs: 5, md: 7 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} justifyContent="space-between" spacing={3} sx={{ mb: 5 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box sx={{ width: 22, height: 2.5, bgcolor: '#f97316', borderRadius: 1 }} />
                <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 2.5 }}>
                  CV. Mulia Utama Mandiri
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.4rem' }, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em', mb: 1 }}>
                {t('kandidat.list.title')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', maxWidth: 480, lineHeight: 1.65 }}>
                {t('kandidat.list.subtitle')}
              </Typography>
            </Box>

            {/* Live count badge */}
            <Box sx={{
              px: 3, py: 2, borderRadius: '16px',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              textAlign: 'center', flexShrink: 0,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4ade80', boxShadow: '0 0 8px rgba(74,222,128,0.6)' }} />
                <Typography sx={{ color: '#4ade80', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                  {t('kandidat.list.talentaAktif')}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: '2.2rem', color: '#fff', lineHeight: 1, letterSpacing: '-0.04em' }}>
                {candidates.length}
              </Typography>
            </Box>
          </Stack>

          {/* Search bar */}
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '16px',
            overflow: 'hidden',
          }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="stretch">
              <Box sx={{ flex: 1, px: 2, py: 1.25 }}>
                <TextField
                  fullWidth size="small"
                  placeholder={t('kandidat.list.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff', fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '& input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                    },
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} /></InputAdornment>,
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <CloseIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
              <Box sx={{
                px: 2, py: 1.5,
                borderTop: { xs: '1px solid rgba(255,255,255,0.08)', sm: 'none' },
                borderLeft: { sm: '1px solid rgba(255,255,255,0.08)' },
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.75,
              }}>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.35)',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  mr: 0.5,
                  whiteSpace: 'nowrap',
                  width: { xs: '100%', sm: 'auto' },
                  mb: { xs: 0.25, sm: 0 },
                }}>
                  {t('kandidat.list.filterLabel')}
                </Typography>
                {profesiOptions.map(option => {
                  const active = selectedProfesi.includes(option);
                  const color  = getProfesiColor(option);
                  return (
                    <Box key={option} onClick={() => toggleProfesi(option)} sx={{
                      px: 1.5, py: 0.55, borderRadius: 99, cursor: 'pointer',
                      bgcolor: active ? color : 'rgba(255,255,255,0.08)',
                      border: `1px solid ${active ? color : 'rgba(255,255,255,0.1)'}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: active ? color : 'rgba(255,255,255,0.14)' },
                    }}>
                      <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.76rem' }, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                        {getProfesiLabel(option, t)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* ── CANDIDATE GRID ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        {/* Result count */}
        <Typography sx={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600, mb: 4 }}>
          {t('kandidat.list.resultCount', { count: filtered.length })}
        </Typography>

        <Grid container spacing={3}>
          {filtered.map((c) => {
            const color = getProfesiColor(c.profesi);
            return (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Box
                  onClick={() => setModalCandidate(c)}
                  sx={{
                    p: '5px',
                    borderRadius: '22px',
                    bgcolor: '#f1f5f9',
                    boxShadow: '0 1px 3px rgba(15,23,42,0.05), 0 4px 16px -6px rgba(15,23,42,0.06)',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex', flexDirection: 'column',
                    transition: 'background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      bgcolor: `${color}1a`,
                      boxShadow: `0 10px 28px -8px ${color}30, 0 2px 6px rgba(15,23,42,0.04)`,
                    },
                  }}
                >
                  <Box sx={{
                    borderRadius: '17px',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}>

                  {/* Photo */}
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedPhotoId(prev => prev === c.id ? null : c.id);
                    }}
                    sx={{
                      position: 'relative',
                      minHeight: 300,
                      maxHeight: expandedPhotoId === c.id ? 700 : 300,
                      overflow: 'hidden',
                      flexShrink: 0,
                      cursor: expandedPhotoId === c.id ? 'zoom-out' : 'zoom-in',
                      transition: 'max-height 0.4s ease',
                    }}
                  >
                    <Box
                      component="img"
                      src={candidateImages[c.slug] || c.foto}
                      alt={c.namaLengkap}
                      sx={{ width: '100%', height: 'auto', display: 'block', objectPosition: 'top' }}
                    />
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(to top, rgba(8,14,28,0.88) 0%, rgba(8,14,28,0.15) 50%, transparent 100%)',
                    }} />

                    {/* Status badge */}
                    <Box sx={{
                      position: 'absolute', top: 14, right: 14,
                      display: 'flex', alignItems: 'center', gap: 0.6,
                      px: 1.3, py: 0.5, borderRadius: 99,
                      bgcolor: 'rgba(8,14,28,0.6)', backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                    }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80', flexShrink: 0 }} />
                      <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5 }}>
                        {c.statusSDM || 'Tersedia'}
                      </Typography>
                    </Box>

                    {/* Name overlay */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 2.5, pb: 2.5, zIndex: 1 }}>
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center',
                        px: 1.25, py: 0.4, borderRadius: 99,
                        bgcolor: color, mb: 1.25,
                      }}>
                        <Typography sx={{ color: '#fff', fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.4 }}>
                          {getProfesiLabel(c.profesi, t)}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.3rem', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                        {c.namaPanggilan}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.74rem', mt: 0.3 }}>
                        {c.namaLengkap}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Card body */}
                  <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Quick stats */}
                    <Stack direction="row" spacing={2} sx={{ mb: 2.5 }} divider={<Box sx={{ width: '1px', bgcolor: '#e2e8f0' }} />}>
                      {[
                        { icon: <CakeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, val: `${c.umur} thn` },
                        { icon: <GroupsIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, val: c.suku },
                        { icon: <SchoolIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, val: c.pendidikanTerakhir },
                      ].map((s, i) => (
                        <Stack key={i} direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                          {s.icon}
                          <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.val}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    {/* Skills */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 3 }}>
                      {c.keahlian.slice(0, 3).map((skill, i) => (
                        <Box key={i} sx={{
                          px: 1.25, py: 0.4, borderRadius: '6px',
                          bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                          maxWidth: 140, overflow: 'hidden',
                        }}>
                          <Typography sx={{
                            fontSize: '0.72rem', fontWeight: 600, color: '#475569',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {skill}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                      <Button
                        fullWidth variant="contained"
                        onClick={(e) => { e.stopPropagation(); setModalCandidate(c); }}
                        sx={{
                          py: 1.15, borderRadius: '10px', textTransform: 'none',
                          fontWeight: 700, fontSize: '0.85rem',
                          bgcolor: THEME.dark, boxShadow: 'none',
                          '&:hover': { bgcolor: '#1e293b', boxShadow: 'none' },
                          transition: 'background 0.2s ease',
                        }}
                      >
                        {t('kandidat.list.detailButton')}
                      </Button>
                      <Tooltip title={t('kandidat.list.consultButton')} arrow>
                        <Button
                          variant="outlined"
                          component="a" target="_blank" rel="noopener noreferrer"
                          href={`https://wa.me/${t('footer.number')}?text=${encodeURIComponent(
                            t('kandidat.list.consultMessage', { name: c.namaLengkap, profesi: c.profesi })
                          )}`}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            minWidth: 44, px: 1.25, py: 1.15, borderRadius: '10px',
                            color: '#25D366', borderColor: '#dcfce7',
                            bgcolor: '#f0fdf4',
                            '&:hover': { bgcolor: '#dcfce7', borderColor: '#86efac' },
                            transition: 'background 0.2s ease',
                          }}
                        >
                          <WhatsAppIcon sx={{ fontSize: 20 }} />
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {modalCandidate && (() => {
        const idx = candidates.findIndex(c => c.id === modalCandidate.id);
        return (
          <CandidateModal
            candidate={modalCandidate}
            onClose={() => setModalCandidate(null)}
            onPrev={idx > 0 ? () => setModalCandidate(candidates[idx - 1]) : null}
            onNext={idx < candidates.length - 1 ? () => setModalCandidate(candidates[idx + 1]) : null}
            t={t}
          />
        );
      })()}
    </Box>
  );
}
